/**
 * AttachmentImagePreview — spec rev160 (2026-02-07).
 *
 * Wrapper qui affiche un aperçu miniature agrandi au survol d'une
 * pièce jointe de type image. Utilisé dans TaskDetail et AviDetail :
 * enveloppe le badge/vignette de l'attachment, et si le fichier est
 * bien une image ET qu'on peut résoudre son URL blob via l'API de
 * download, le popup montre un thumbnail 220×220 max au survol.
 *
 * Le blob n'est fetché QUE la première fois où l'utilisateur survole
 * le composant (lazy) pour éviter de saturer B2 sur des pages
 * chargées avec 50+ pièces jointes.
 *
 * Props :
 *   • children      : nœud à envelopper (badge / miniature existante)
 *   • filename      : nom pour le test d'extension via getFileTypeMeta
 *   • downloadUrl   : URL API à charger comme blob (ex.
 *                     `/api/avi/{id}/attachments/{aid}/download`)
 *   • side          : "top"|"bottom"|"left"|"right" (défaut "top")
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { getFileTypeMeta } from "@/lib/fileTypeMeta";

export default function AttachmentImagePreview({
  children, filename, downloadUrl, side = "top",
}) {
  const { isImage } = getFileTypeMeta(filename);
  const [hover, setHover] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    // Cleanup blob URL au démontage.
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    if (fetchedRef.current || !isImage || !downloadUrl) return;
    fetchedRef.current = true;
    setLoading(true);
    try {
      const resp = await api.get(downloadUrl, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      setBlobUrl(url);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  if (!isImage) {
    // Non-image → no wrapper (juste children pass-through).
    return children;
  }

  const positionStyle = {
    top:    { bottom: "calc(100% + 8px)", left: 0 },
    bottom: { top:    "calc(100% + 8px)", left: 0 },
    left:   { right:  "calc(100% + 8px)", top: 0 },
    right:  { left:   "calc(100% + 8px)", top: 0 },
  }[side];

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => { setHover(true); load(); }}
      onMouseLeave={() => setHover(false)}
      data-testid="attachment-image-preview"
    >
      {children}
      {hover && (
        <span
          className="absolute z-50 pointer-events-none rounded-md overflow-hidden"
          style={{
            ...positionStyle,
            background: "#0B0B0C",
            border: "1px solid #2A2A2A",
            boxShadow: "0 8px 32px rgba(0,0,0,0.55)",
            padding: 4,
          }}
          data-testid="attachment-image-preview-popover"
        >
          {loading ? (
            <div
              className="flex items-center justify-center text-[11px]"
              style={{ width: 180, height: 120, color: "#9CA3AF" }}
            >
              Chargement…
            </div>
          ) : blobUrl ? (
            <img
              src={blobUrl}
              alt={filename || ""}
              style={{
                maxWidth: 220, maxHeight: 220,
                display: "block", borderRadius: 4,
              }}
            />
          ) : (
            <div
              className="flex items-center justify-center text-[11px]"
              style={{ width: 180, height: 60, color: "#EF4444" }}
            >
              Aperçu indisponible
            </div>
          )}
        </span>
      )}
    </span>
  );
}
